import {
  useAIVisibilityCompetitorShare,
  useAIVisibilityQuestions,
  useAIVisibilityScore,
} from '../hooks/useAIVisibility';
import type { TrackedQuestion } from '../api/visibilityApi';
import styles from './RankingsSection.module.css';

/** A move up shows ▲, down ▼. A question not mentioned last week has no number to compare. */
function Movement({ now, before }: { now: number; before: number | null }) {
  const moved = before === null ? 0 : before - now;
  if (moved > 0) return <span className={styles.up}>▲{moved}</span>;
  if (moved < 0) return <span className={styles.down}>▼{-moved}</span>;
  return null;
}

function Answer({ question }: { question: TrackedQuestion }) {
  const { now, prev } = question.results.chatgpt;
  if (!now) return <p className={styles.muted}>Not asked this week.</p>;
  const named = now.topResult && now.position !== 1 ? now.topResult : null;
  return (
    <>
      {now.appeared && now.position !== null ? (
        <p className={styles.answer}>
          You were named <span className={styles.rank}>#{now.position}</span>
          <Movement now={now.position} before={prev?.position ?? null} />
        </p>
      ) : (
        <p className={styles.muted}>You were not mentioned.</p>
      )}
      {named && <p className={styles.note}>ChatGPT named {named} first.</p>}
    </>
  );
}

/** The patient questions ChatGPT was asked for one search term, and what it answered. */
export function TermAiAnswers({ term }: { term: string }) {
  const { data, isLoading } = useAIVisibilityQuestions();
  const questions = (data ?? []).filter((question) => question.searchTerm === term);

  return (
    <section className={styles.panelSection}>
      <h3 className={styles.panelTitle}>How ChatGPT answered</h3>
      {isLoading && <p className={styles.muted}>Loading…</p>}
      {!isLoading && questions.length === 0 && (
        <p className={styles.muted}>
          No patient questions have been asked for this term yet. They are written and asked with
          the weekly check.
        </p>
      )}
      {questions.map((question) => (
        <div key={question.id} className={styles.aheadGroup}>
          <p className={styles.query}>
            <strong>&ldquo;{question.promptText}&rdquo;</strong>
          </p>
          <Answer question={question} />
        </div>
      ))}
    </section>
  );
}

/** How often ChatGPT recommends the practice across all its patient questions, and who it names first. */
export function AiAnswersSummary() {
  const score = useAIVisibilityScore();
  const share = useAIVisibilityCompetitorShare();
  const data = score.data;
  if (!data?.hasEverRun) return null;

  const change = data.scoreNow - data.scoreLastWeek;
  const named = (share.data ?? []).filter((row) => row.isYou || (!row.isOther && row.count > 0));

  return (
    <section aria-label="What ChatGPT recommends" className={styles.summary}>
      <h2 className={styles.title}>What ChatGPT recommends</h2>
      <p className={styles.description}>
        ChatGPT recommended you for{' '}
        <strong>
          {data.questionsAppearedNow} of {data.questionsTotal}
        </strong>{' '}
        patient questions this week ({data.scoreNow}%
        {change > 0 && <span className={styles.up}>▲{change} pts</span>}
        {change < 0 && <span className={styles.down}>▼{-change} pts</span>}). Click a term above to
        see each question.
      </p>
      {named.length > 0 && (
        <>
          <h3 className={styles.aheadTitle}>Who ChatGPT names first</h3>
          <ul className={styles.aheadList}>
            {named.map((row) => (
              <li key={row.name} className={row.isYou ? styles.rank : undefined}>
                {row.name}
                {row.isYou ? ' (you)' : ''} · {row.pct}%
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
